// client/src/pages/FaqPage.js
// 자주 묻는 질문 — v5 리디자인 (B안 확정: 검색 + 인기 질문 + 입장 탭)
// 콘텐츠는 src/data/faqData.js 한 곳에서 관리 (기존 그대로), 역할 인지 헤더/푸터 유지.
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import InfluencerHomeHeader from '../components/influencer/InfluencerHomeHeader';
import MarketerHomeHeader from '../components/marketer/MarketerHomeHeader';
import { PointProvider } from '../contexts/PointContext';
import Footer from '../components/landing/Footer';
import faqItems from '../data/faqData';
import '../styles/public/pub.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function RoleHeader() {
  const [role, setRole] = useState(() => {
    try { return sessionStorage.getItem('current_user_role'); } catch { return null; }
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/user/me`, { credentials: 'include' });
        const j = await res.json().catch(() => ({}));
        if (!alive) return;
        const r = (res.ok && j?.ok && j?.user?.role) ? j.user.role : null;
        if (r) {
          setRole(r);
          try { sessionStorage.setItem('current_user_role', r); } catch {}
        }
      } catch {}
    })();
    return () => { alive = false; };
  }, []);

  if (role === 'influencer') {
    return (
      <PointProvider userRole="influencer">
        <InfluencerHomeHeader />
      </PointProvider>
    );
  }
  if (role === 'marketer') {
    return (
      <PointProvider userRole="marketer">
        <MarketerHomeHeader />
      </PointProvider>
    );
  }
  return <Header />;
}

const SIDE_META = [
  { key: 'marketer', label: '광고주' },
  { key: 'influencer', label: '인플루언서' },
  { key: 'common', label: '공통' },
];

const SIDE_BADGE = {
  marketer: { label: '광고주', cls: 'badge--blue' },
  influencer: { label: '인플루언서', cls: 'badge--mint' },
  common: { label: '공통', cls: 'badge--gray' },
};

// 인기 질문 (검색어 프리셋 — B안)
const HOT_QUERIES = ['환전', '수수료', '검수'];

// 데이터에 존재하는 입장만 탭으로 노출 (빈 분류 방지)
const SIDES = [
  { key: 'all', label: '전체' },
  ...SIDE_META.filter((s) => faqItems.some((it) => it.side === s.key)),
];

function FaqPage() {
  const nav = useNavigate();
  const [side, setSide] = useState('all');
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState(null);

  const groups = useMemo(() => {
    let visible = side === 'all' ? faqItems : faqItems.filter((it) => it.side === side);
    const q = query.trim().toLowerCase();
    if (q) {
      visible = visible.filter(
        (it) => it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q)
      );
    }
    const map = new Map();
    visible.forEach((it) => {
      if (!map.has(it.category)) map.set(it.category, []);
      map.get(it.category).push(it);
    });
    return Array.from(map, ([category, items]) => ({ category, items }));
  }, [side, query]);

  const handleSide = (key) => {
    setSide(key);
    setOpenId(null);
  };

  const handleHot = (q) => {
    setQuery(q);
    setSide('all');
    setOpenId(null);
  };

  const toggle = (id) => setOpenId((cur) => (cur === id ? null : id));

  const isEmpty = groups.length === 0;

  return (
    <div className="pub">
      <RoleHeader />

      <main className="wrap">
        <div className="pub-head">
          <h1>자주 묻는 질문</h1>
          <p>궁금한 점을 빠르게 해결하세요. 못 찾으셨다면 문의하기를 이용해주세요.</p>
        </div>

        {/* 검색 (B안) */}
        <div className="faqsearch">
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpenId(null); }}
            placeholder="무엇이 궁금하세요? 예: 환전, 수수료"
            aria-label="질문 검색"
          />
          <i aria-hidden="true">🔍</i>
        </div>

        {/* 인기 질문 (B안) */}
        <div className="hotq">
          {HOT_QUERIES.map((q) => (
            <button key={q} type="button" onClick={() => handleHot(q)}>
              <b>인기</b>{q}
            </button>
          ))}
        </div>

        {/* 입장 탭 */}
        <div className="faq-tabs" role="tablist" aria-label="질문 분류 선택">
          {SIDES.map((s) => (
            <button
              key={s.key}
              type="button"
              role="tab"
              aria-selected={side === s.key}
              className={side === s.key ? 'is-active' : ''}
              onClick={() => handleSide(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="faqlist">
          {isEmpty && (
            <div className="card" style={{ padding: '48px 24px', textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#3b4658', marginBottom: 6 }}>
                검색 결과가 없어요
              </div>
              <div className="t-sub">다른 키워드로 검색하거나 문의하기를 이용해주세요.</div>
            </div>
          )}

          {groups.map((group) => (
            <section key={group.category}>
              <div className="faq__cat">{group.category}</div>
              {group.items.map((it) => {
                const isOpen = openId === it.id;
                const badge = SIDE_BADGE[it.side];
                return (
                  <details
                    key={it.id}
                    open={isOpen}
                    onToggle={(e) => {
                      if (e.target.open && openId !== it.id) setOpenId(it.id);
                      else if (!e.target.open && openId === it.id) setOpenId(null);
                    }}
                  >
                    <summary
                      onClick={(e) => { e.preventDefault(); toggle(it.id); }}
                    >
                      <span className="q" aria-hidden="true">Q</span>
                      <span className="qt">{it.q}</span>
                      {badge && <span className={`badge ${badge.cls} who`}>{badge.label}</span>}
                    </summary>
                    <div className="a">
                      {it.a.split('\n\n').map((para, i) => (
                        <p key={i}>{para}</p>
                      ))}
                    </div>
                  </details>
                );
              })}
            </section>
          ))}
        </div>

        <div className="faq-cta">
          <b>원하는 답을 못 찾으셨나요?</b>
          <span className="t-sub">문의를 남기면 등록된 메일 또는 연락처로 답변드려요.</span>
          <button type="button" className="btn btn--blue" onClick={() => nav('/inquiry')}>
            문의하기
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default FaqPage;
